import { inject, Injectable } from '@angular/core';
import { Network } from '@capacitor/network';
import { BehaviorSubject, combineLatest, filter, Subject, takeUntil } from 'rxjs';
import { TravelDocumentSynchronizeService } from "@core/services/synchronize/travel-document-synchronize.service";
import { TripSynchronizeService } from "@core/services/synchronize/trip-synchronize.service";
import { WorkTimeEntrySynchronizeService } from "@core/services/synchronize/work-time-entry-synchronize.service";
import { AuthenticationService } from "@core/services/authentication.service";
import { toObservable } from "@angular/core/rxjs-interop";
import { environment } from "@env/environment";

@Injectable({
  providedIn: 'root'
})
export class NetworkService {
  #travelDocumentSynchronizer = inject(TravelDocumentSynchronizeService);
  #tripSynchronizer = inject(TripSynchronizeService);
  #workTimeEntrySynchronizer = inject(WorkTimeEntrySynchronizeService);

  #authService = inject(AuthenticationService);
  #isOnlineSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(navigator.onLine);
  #destroy$ = new Subject<void>();
  isOnline = this.#isOnlineSubject.value;
  isOnline$ = this.#isOnlineSubject.asObservable();

  init() {
    Network.addListener('networkStatusChange', status => {
      this.#isOnlineSubject.next(status.connected);
    });

    combineLatest([
      this.#isOnlineSubject.asObservable(),
      toObservable(this.#authService.isLoggedIn)
    ])
      .pipe(
        takeUntil(this.#destroy$),
        filter(() => environment.autoSync),
        filter(([isOnline, isLoggedIn]) => isOnline && isLoggedIn)
      )
      .subscribe(() => {
        this.synchronize();
    });

    this.#isOnlineSubject.next(navigator.onLine);
    if (navigator.onLine && this.#authService.isLoggedIn() && environment.autoSync) {
      this.synchronize();
    }
  }

  private synchronize(): void {
    this.#travelDocumentSynchronizer.synchronize().subscribe();
    this.#tripSynchronizer.synchronize().subscribe();
    this.#workTimeEntrySynchronizer.synchronize().subscribe();
  }

  destroy() {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
